import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';
import { toast } from 'react-toastify';

export interface Worker {
  worker_id: number;
  worker_name: string;
  priority?: number;
}

export interface Connection {
  connection_id: number;
  connection_name: string;
  connection_type: string;
  connection_parameters: Record<string, any>;
  workers: Worker[];
  // Folder support (nap-backend #1076). Not zone-scoped. `folder_id` is
  // always sent (null clears); `folder_path` is response-only display data.
  folder_id: number | null;
  folder_path?: FolderPath | null;
}

interface PaginationState {
  total_records: number;
  total_pages: number;
  current_page: number;
  limit: number;
  offset: number;
}

interface ConnectionConfig {
  header_key: string;
  header_type: string;
  label: string;
}

interface ConnectionsState {
  items: Connection[];
  sqlPlusUsernames: string[];
  status: string | null;
  error: string | null;
  pagination?: PaginationState;
  config?: ConnectionConfig[];
  filtersConfig?: FilterConfig[];
  loading: {
    fetchConnections: boolean;
    fetchConnectionsPaginated: boolean;
    fetchSqlPlusUsernames: boolean;
  };
}

const initialState: ConnectionsState = {
  items: [],
  sqlPlusUsernames: [],
  status: null,
  error: null,
  loading: {
    fetchConnections: false,
    fetchConnectionsPaginated: false,
    fetchSqlPlusUsernames: false,
  },
};

export const fetchConnections = createAsyncThunk('connections/fetchConnections', async () => {
  const response = await api.get('/connections');
  return response.data.connections as Connection[];
});

export const fetchConnectionsPaginated = createAsyncThunk('connections/fetchConnectionsPaginated', async (params: Record<string, any> = {}) => {
  const cleanParams = {
    ...params,
    sortColumn: params.sortColumn || 'created_at',
    sortOrder: (params.sortOrder || 'ASC').toUpperCase(),
    limit: params.limit || 2000,
    page: params.page || 1,
  };

  const res = await api.get('/connections', {
    params: flattenQuery(cleanParams),
  });

  return res.data as {
    connections: Connection[];
    pagination: PaginationState;
    config?: ConnectionConfig[];
    filtersConfig?: FilterConfig[];
  };
});

export const fetchSqlPlusUsernames = createAsyncThunk('connections/fetchSqlPlusUsernames', async () => {
  const response = await api.get('/connections', {
    params: { connection_type: 'sqlplus', limit: 2000 },
  });

  const connections: Connection[] = response.data.connections || [];

  return [...new Set(connections.map((c) => c.connection_parameters?.username).filter(Boolean))].sort((a, b) => a.localeCompare(b));
});

export const createConnection = createAsyncThunk('connections/createConnection', async (connection: Omit<Connection, 'connection_id'>, { rejectWithValue }) => {
  try {
    const response = await api.post('/connections', connection);
    return response.data as Connection;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

export const updateConnection = createAsyncThunk('connections/updateConnection', async (connection: Connection, { rejectWithValue }) => {
  try {
    const response = await api.put(`/connections/${connection.connection_id}`, connection);
    return response.data as Connection;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

export const deleteConnection = createAsyncThunk('connections/deleteConnection', async (connection_name: string, { rejectWithValue }) => {
  try {
    await api.delete(`/connections/${connection_name}`);
    return connection_name;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: err.message });
  }
});

const connectionsSlice = createSlice({
  name: 'connections',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnections.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchConnections = true;
      })
      .addCase(fetchConnections.fulfilled, (state, action) => {
        state.items = action.payload;
        state.status = 'succeeded';
        state.loading.fetchConnections = false;
      })
      .addCase(fetchConnections.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message || 'Failed to fetch connections';
        state.loading.fetchConnections = false;
      });

    builder
      .addCase(fetchConnectionsPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchConnectionsPaginated = true;
      })
      .addCase(fetchConnectionsPaginated.fulfilled, (state, action) => {
        const { connections, pagination, config, filtersConfig } = action.payload;
        state.items = connections || [];
        state.pagination = pagination;
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];
        state.status = 'succeeded';
        state.loading.fetchConnectionsPaginated = false;
      })
      .addCase(fetchConnectionsPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message || 'Failed to fetch paginated connections';
        state.loading.fetchConnectionsPaginated = false;
      });

    builder
      .addCase(fetchSqlPlusUsernames.pending, (state) => {
        state.loading.fetchSqlPlusUsernames = true;
        state.error = null;
      })
      .addCase(fetchSqlPlusUsernames.fulfilled, (state, action) => {
        state.sqlPlusUsernames = action.payload;
        state.loading.fetchSqlPlusUsernames = false;
      })
      .addCase(fetchSqlPlusUsernames.rejected, (state, action) => {
        state.error = action.error?.message || 'Failed to fetch SQL*Plus usernames';
        state.loading.fetchSqlPlusUsernames = false;
      });

    builder
      .addCase(createConnection.fulfilled, (state, action) => {
        state.items.push(action.payload);
        const toastId = 'data-create-connection';
        if (!toast.isActive(toastId)) {
          toast.success('Connection created successfully!', { toastId });
        }
      })
      .addCase(createConnection.rejected, (state, action) => {
        const payload: any = action.payload;
        state.error = payload?.msg || payload?.error || payload?.message || 'Failed to create connection';
        const toastId = 'data-failed-to-create-connection';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to create connection! ${state.error}`, {
            toastId,
          });
        }
      });

    builder
      .addCase(updateConnection.fulfilled, (state, action) => {
        const idx = state.items.findIndex((c) => c.connection_id === action.payload.connection_id);
        if (idx !== -1) state.items[idx] = action.payload;
        const toastId = 'data-update-connection';
        if (!toast.isActive(toastId)) {
          toast.success('Connection updated successfully!', { toastId });
        }
      })
      .addCase(updateConnection.rejected, (state, action) => {
        const payload: any = action.payload;
        state.error = payload?.msg || payload?.error || payload?.message || 'Failed to update connection';
        const toastId = 'data-failed-to-update-connection';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to update connection! ${state.error}`, {
            toastId,
          });
        }
      });

    builder
      .addCase(deleteConnection.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c.connection_name !== action.payload);
        const toastId = 'data-delete-connection';
        if (!toast.isActive(toastId)) {
          toast.success('Connection deleted successfully!', { toastId });
        }
      })
      .addCase(deleteConnection.rejected, (state, action) => {
        const payload: any = action.payload;
        state.error = payload?.msg || payload?.error || payload?.message || 'Failed to delete connection';
        const toastId = 'data-failed-to-delete-connection';
        if (!toast.isActive(toastId)) {
          toast.error(`Failed to delete connection! ${state.error}`, {
            toastId,
          });
        }
      });
  },
});

export default connectionsSlice.reducer;
